/** @jsxRuntime classic */
/** @jsx jsx */
import { css, jsx } from "@emotion/react";
import { useI18n } from "hooks/i18n/i18n";
import React, { useState } from "react";

import type { CardInfo } from "modules/purchase";

type Props = {
  registeredCardList: CardInfo[] | null;
  isDarkModeEnabled: boolean;
};

export const StreamingLiveCommerceCreditPaymentV2: React.FC<Props> = ({
  registeredCardList,
  isDarkModeEnabled,
}) => {
  const [selectedCardType, setSelectedCardType] = useState<
    "registered" | "new"
  >("new");

  const classesCreditPaymentDesign = creditPaymentDesign({ isDarkModeEnabled });
  const { t } = useI18n();

  return (
    <div css={classesCreditPaymentDesign.container}>
      <p css={classesCreditPaymentDesign.subLabel}>
        {t("streaming.commerceCart.cardSelect")}
      </p>

      <div css={classesCreditPaymentDesign.selectionBox}>
        <input
          type="radio"
          id="card-registered"
          name="cardSelection"
          checked={selectedCardType === "registered"}
          onChange={() => setSelectedCardType("registered")}
          css={classesCreditPaymentDesign.radioInput}
        />
        <label
          htmlFor="card-registered"
          css={classesCreditPaymentDesign.radioLabel}
        >
          {t("streaming.commerceCart.registeredCard")}
        </label>
      </div>

      {selectedCardType === "registered" && registeredCardList && (
        <div css={classesCreditPaymentDesign.formContainer}>
          <div css={classesCreditPaymentDesign.inputGroup}>
            {registeredCardList.length > 0 ? (
              <React.Fragment>
                <select css={classesCreditPaymentDesign.inputSelect}>
                  {registeredCardList.map((card, index) => (
                    <option value={card.cardSeq} key={index}>
                      **** **** **** {card.cardNumber.slice(-4)}
                    </option>
                  ))}
                </select>
              </React.Fragment>
            ) : (
              <p>{t("streaming.commerceCart.noRegisteredCard")}</p>
            )}
          </div>
        </div>
      )}

      <div css={classesCreditPaymentDesign.selectionBox}>
        <input
          type="radio"
          id="card-new"
          name="cardSelection"
          checked={selectedCardType === "new"}
          onChange={() => setSelectedCardType("new")}
          css={classesCreditPaymentDesign.radioInput}
        />
        <label htmlFor="card-new" css={classesCreditPaymentDesign.radioLabel}>
          {t("streaming.commerceCart.newCard")}
        </label>
      </div>

      {selectedCardType === "new" && (
        <div css={classesCreditPaymentDesign.formContainer}>
          <div css={classesCreditPaymentDesign.inputGroup}>
            <label>{t("streaming.commerceCart.cardNumber")}</label>
            <input
              type="text"
              placeholder="1234123412341234"
              css={classesCreditPaymentDesign.textInput}
            />
            <div css={classesCreditPaymentDesign.brandImageWrapper}>
              <img
                src="/images/liveCommerce/paymentMethodCard.png"
                alt="Card Brands"
                css={classesCreditPaymentDesign.brandImage}
              />
            </div>
          </div>

          <div css={classesCreditPaymentDesign.inputGroup}>
            <label>{t("streaming.commerceCart.cardExpiration")}</label>
            <div css={classesCreditPaymentDesign.flexRow}>
              <input
                type="text"
                placeholder="MM"
                css={classesCreditPaymentDesign.smallInput}
              />
              <input
                type="text"
                placeholder="YY"
                css={classesCreditPaymentDesign.smallInput}
              />
            </div>
          </div>

          <div css={classesCreditPaymentDesign.inputGroup}>
            <label>{t("streaming.commerceCart.cardHolderName")}</label>
            <input
              type="text"
              placeholder="TARO YAMADA"
              css={classesCreditPaymentDesign.textInput}
            />
          </div>

          <div css={classesCreditPaymentDesign.inputGroup}>
            <label>{t("streaming.commerceCart.cardCVV")}</label>
            <input
              type="text"
              placeholder="123"
              css={[
                classesCreditPaymentDesign.textInput,
                css`
                  width: 80px;
                `,
              ]}
            />
          </div>
        </div>
      )}
    </div>
  );
};

type CreditPaymentStylesProps = {
  isDarkModeEnabled: boolean;
};

const creditPaymentDesign = (props: CreditPaymentStylesProps) => {
  const theme = {
    text: props.isDarkModeEnabled ? "#FFFFFF" : "#000000",
    bg: props.isDarkModeEnabled ? "#000000" : "#FFFFFF",
    inputBg: props.isDarkModeEnabled ? "#1A1A1A" : "#F0F0F0",
    border: props.isDarkModeEnabled ? "#FFFFFF" : "#000000",
  };

  const container = css`
    padding: 16px 0;
    margin: 0 16px;
    border-top: 1px solid ${theme.border};
  `;
  const subLabel = css`
    color: ${theme.text};
    margin-bottom: 12px;
  `;
  const selectionBox = css`
    margin-bottom: 8px;
  `;
  const radioInput = css`
    display: none;
    &:checked + label::before {
      background-color: ${theme.text};
    }
    &:checked + label::after {
      content: "";
      position: absolute;
      top: 50%;
      left: 29px;
      transform: translateY(-50%);
      width: 6px;
      height: 6px;
      background-color: ${theme.bg};
      border-radius: 50%;
    }
  `;
  const radioLabel = css`
    position: relative;
    display: flex;
    align-items: center;
    width: 100%;
    padding: 16px 16px 16px 56px;
    background-color: ${theme.bg};
    color: ${theme.text};
    font-size: 14px;
    cursor: pointer;
    box-sizing: border-box;
    &::before {
      content: "";
      position: absolute;
      top: 50%;
      left: 20px;
      transform: translateY(-50%);
      width: 24px;
      height: 24px;
      border: 2px solid ${theme.border};
      border-radius: 50%;
      box-sizing: border-box;
    }
  `;
  const formContainer = css`
    margin-top: 20px;
    padding-left: 20px;
  `;
  const inputGroup = css`
    margin-bottom: 16px;
    label {
      display: block;
      font-size: 14px;
      margin-bottom: 8px;
      color: ${theme.text};
    }
  `;
  const textInput = css`
    &[type="text"] {
      background-color: ${theme.bg};
      padding: 0px 16px;
      border-radius: 10px;
      height: 40px;
    }
    border: none;
    color: ${theme.text};
    font-size: 16px;
    &::placeholder {
      color: #a3a3a3;
    }
  `;

  const inputSelect = css`
    appearance: none;
    background-color: ${theme.bg};
    padding: 0px 16px;
    border-radius: 10px;
    height: 40px;
    border: none;
    color: ${theme.text};
    font-size: 16px;
    width: 100%;
  `;
  const flexRow = css`
    display: flex;
    gap: 12px;
  `;
  const smallInput = css`
    &[type="text"] {
      background-color: ${theme.bg};
      padding: 0px 16px;
      border-radius: 10px;
      height: 40px;
    }
    font-size: 16px;
    width: 70px;
    color: ${theme.text};
    &::placeholder {
      color: #a3a3a3;
    }
  `;
  const brandImageWrapper = css`
    margin-top: 8px;
  `;
  const brandImage = css`
    height: 24px;
    object-fit: contain;
  `;

  return {
    container,
    subLabel,
    selectionBox,
    radioInput,
    radioLabel,
    formContainer,
    inputGroup,
    textInput,
    inputSelect,
    flexRow,
    smallInput,
    brandImageWrapper,
    brandImage,
  };
};
